import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import axios from 'axios'
import { format } from 'libphonenumber-js'

import { QUERY_KEY } from '../constants'
import type { components, operations } from '../constants/schema'

import { toast, updateToken } from '../utils'
import { abrSetUser, abrUserSignup, abrUserSignout } from '../utils/abr'
import { queryClient } from '../utils/queryClient'

import { getUserInfo } from './my'

type OAuthSignInParams = Required<operations['AuthController_webUrl']['parameters']>['query']
type OAuthVerifyParams = Required<operations['AuthController_webVerify']['parameters']>['query']

/**
 * 회원가입
 * @param data ReqUserAddUserTerms
 * @returns UserDto | undefined
 */
export const signUp = async (data: components['schemas']['ReqUserAddUserTerms']) => {
  try {
    const response = (await axios.post<components['schemas']['UserDto']>('/auth/signup', data)).data

    //Airbridge 유저 업데이트
    abrSetUser(response)

    //알림톡 유저 프로필 업데이트
    const atProfile: Record<string, any> = {}
    if (response.name) atProfile.name = response.name
    if (response.email) atProfile.email = response.email
    if (response.identification?.danalPhoneNumber) {
      atProfile.mobileNumber = format(response.identification.danalPhoneNumber, 'KR', 'E.164')
    }
    ChannelService.updateUser({ language: 'ko', profile: atProfile })

    abrUserSignup({ provider: response.auth.provider })
    ChannelService.track('signup')

    return response
  } catch (error) {
    if (axios.isAxiosError(error)) {
      toast(error.response?.data.message, error)
    }
  }
}

/**
 * OAuth 로그인 1
 * @param params OAuthSignInParams
 */
export const oAuthSignIn = async (params: OAuthSignInParams) => {
  try {
    const { url } = (await axios.get<components['schemas']['ResAuthWebUrl']>('/auth/web/url', { params })).data
    window.location.href = url
  } catch (error) {
    if (axios.isAxiosError(error)) {
      toast(error.response?.data.message, error)
    }
  }
}

/**
 * OAuth 로그인 2
 * @param params OAuthVerifyParams
 * @returns ResAuthWebVerificationInfo | undefined
 */
export const oAuthVerify = async (params: OAuthVerifyParams) => {
  try {
    return (await axios.get<components['schemas']['ResAuthWebVerificationInfo']>('/auth/web/verify', { params })).data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      toast(error.response?.data.message, error)
    }
    return
  }
}

/**
 * 테스트용 local 로그인
 * @param id 로그인 할 유저 id
 * @returns void
 */
export const localLogin = async (id: number) => {
  if (process.env.NODE_ENV !== 'development') {
    return
  }

  try {
    await axios.get(`/auth/login/${id}`)

    await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.checkLogin] })
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEY.userInfo] })

    const userInfo = await getUserInfo()

    if (userInfo?.status === 'NEED_REQUIRE_DATA_INPUT') {
      console.log('NEED_REQUIRE_DATA_INPUT')
      window.location.href = '/signUp'
      return
    }
    window.history.back()
  } catch (error) {
    console.log({ error })
  }
}

/**
 * 현재 로그인 상태인지 확인
 * @returns boolean
 */
export const checkLogin = async () => {
  try {
    const response = (await axios.get<boolean>('/user/check')).data
    return response
  } catch (error) {
    console.log({ error })
  } finally {
    updateToken('save')
  }
  return false
}

/**
 * 로그아웃
 */
export const signOut = async () => {
  try {
    await axios.get('/auth/logout')

    abrUserSignout()
    ChannelService.track('signout')
    ChannelService.shutdown()

    updateToken('remove')

    queryClient.removeQueries({ queryKey: [QUERY_KEY.checkLogin] })
    queryClient.removeQueries({ queryKey: [QUERY_KEY.userInfo] })
  } catch (error) {
    toast('로그아웃에 실패했습니다.', error)
  }
}
